import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Wallet, Ban, Paperclip, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

async function openAttachment(path) {
  const tab = window.open("", "_blank");
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (tab) tab.location.href = url; else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (tab) tab.close();
    toast.error("Could not open the bill photo");
  }
}

export default function SiteExpenses() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("expenses");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/site-expenses`);
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const cancelExpense = async (e) => {
    const reason = window.prompt(`Cancel "${e.description}" (${inr(e.total)})? Give a reason:`);
    if (!reason) return;
    try {
      await api.post(`/projects/${projectId}/site-expenses/${e.id}/cancel`, { reason });
      toast.success("Expense cancelled — cost and accounts reversed");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const cancelPetty = async (p) => {
    if (!window.confirm(`Cancel this petty cash ${p.kind === "TOPUP" ? "top-up" : "return"} of ${inr(p.amount)}?`)) return;
    try {
      await api.post(`/projects/${projectId}/petty-cash/${p.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">{loading ? "Loading…" : ""}</p>;
  const balance = Number(data.petty_balance);

  return (
    <div className="space-y-5" data-testid="site-expenses-page">
      <PageHeader
        title="Site Expenses"
        description="Diesel, food, transport, repairs, daily wages and other small spends at this site. One entry updates the project cost and the company accounts together."
        action={<div className="flex flex-wrap gap-2">
          {data.can_manage && <PettyCashDialog projectId={projectId} balance={balance} onSaved={load} />}
          {data.can_enter && <ExpenseDialog projectId={projectId} categories={data.categories} balance={balance} onSaved={load} />}
        </div>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Petty cash at site</p>
          <p className={`mt-2 font-mono text-lg font-bold ${balance < 0 ? "text-rose-500" : balance < 2000 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>{inr(balance)}</p>
          {balance < 2000 && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Running low</p>}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Spent this month</p>
          <p className="mt-2 font-mono text-lg font-bold text-foreground">{inr(data.spent_this_month)}</p>
        </div>
        <div className="col-span-2 rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">By category (total {inr(data.spent_total)})</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {data.by_category.length === 0 ? <span className="text-muted-foreground">No expenses yet</span> : data.by_category.map((c) => (
              <span key={c.category}><span className="text-muted-foreground">{c.label}</span> <span className="font-mono text-foreground">{inr(c.amount)}</span></span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "expenses" ? "default" : "outline"} onClick={() => setTab("expenses")}>Expenses ({data.expenses.length})</Button>
        <Button size="sm" variant={tab === "petty" ? "default" : "outline"} onClick={() => setTab("petty")}>Petty cash given / returned ({data.petty_cash.length})</Button>
      </div>

      {tab === "expenses" ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead>Paid to / bill</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Paid from</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.expenses.length === 0 && <TableRow><TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">No site expenses yet.</TableCell></TableRow>}
              {data.expenses.map((e) => (
                <TableRow key={e.id} className={e.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`site-expense-row-${e.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{e.expense_date}</TableCell>
                  <TableCell className="text-xs">{e.category_label}</TableCell>
                  <TableCell className="max-w-xs text-sm text-foreground">{e.description}{e.status === "CANCELLED" && <div className="text-[11px] text-rose-500">Cancelled: {e.cancel_reason}</div>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.paid_to || "—"}{e.bill_no ? <div>Bill {e.bill_no}</div> : null}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    <div className="font-semibold text-foreground">{inr(e.total)}</div>
                    {Number(e.gst_amount) > 0 && <div className="text-[10px] text-muted-foreground">incl. GST {inr(e.gst_amount)}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{e.paid_from === "PETTY_CASH" ? "Petty cash" : "Bank"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.entered_by || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {e.has_attachment && <Button size="sm" variant="ghost" title="Bill photo" onClick={() => openAttachment(`/projects/${projectId}/site-expenses/${e.id}/attachment`)}><Paperclip className="h-3.5 w-3.5" /></Button>}
                    {e.status === "POSTED" && data.can_enter && <Button size="sm" variant="ghost" title="Cancel" onClick={() => cancelExpense(e)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Holder</TableHead><TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.petty_cash.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No petty cash given to this site yet.</TableCell></TableRow>}
              {data.petty_cash.map((p) => (
                <TableRow key={p.id} className={p.status === "CANCELLED" ? "opacity-50" : ""}>
                  <TableCell className="text-xs">{p.entry_date}</TableCell>
                  <TableCell className="text-xs">{p.kind === "TOPUP" ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><ArrowDownLeft className="h-3.5 w-3.5" /> Given to site</span> : <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400"><ArrowUpRight className="h-3.5 w-3.5" /> Returned</span>}{p.status === "CANCELLED" && <span className="ml-1 text-rose-500">(cancelled)</span>}</TableCell>
                  <TableCell className="text-sm">{p.holder || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.reference || "—"}{p.remarks ? <div>{p.remarks}</div> : null}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{inr(p.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.entered_by || "—"}</TableCell>
                  <TableCell className="text-right">{p.status === "POSTED" && data.can_manage && <Button size="sm" variant="ghost" onClick={() => cancelPetty(p)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function ExpenseDialog({ projectId, categories, balance, onSaved }) {
  const EMPTY = { expense_date: today(), category: "DIESEL", description: "", paid_to: "", bill_no: "", amount: "", gst_amount: "0", paid_from: "PETTY_CASH" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const total = Number(form.amount || 0) + Number(form.gst_amount || 0);
  const tooMuch = form.paid_from === "PETTY_CASH" && total > balance;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/site-expenses`, {
        ...form, paid_to: form.paid_to || null, bill_no: form.bill_no || null, gst_amount: form.gst_amount || "0",
      });
      const file = fileRef.current?.files?.[0];
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/projects/${projectId}/site-expenses/${data.id}/attachment`, fd); }
        catch (err) { toast.warning(`Saved, but the bill photo failed: ${getErrorMessage(err)}`); }
      }
      toast.success("Expense recorded — project cost and accounts updated");
      setForm(EMPTY);
      if (fileRef.current) fileRef.current.value = "";
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-site-expense-button"><Plus className="h-4 w-4" /> Add expense</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New site expense</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5" data-testid="site-expense-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.expense_date} onChange={set("expense_date")} className="mt-1.5" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="site-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Input required value={form.description} onChange={set("description")} placeholder="e.g. Diesel 100 L for JCB & crane" className="mt-1.5" data-testid="site-expense-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Paid to</Label><Input value={form.paid_to} onChange={set("paid_to")} placeholder="Indian Oil pump, Arakalgudu" className="mt-1.5" /></div>
            <div><Label>Bill no.</Label><Input value={form.bill_no} onChange={set("bill_no")} className="mt-1.5" /></div>
            <div><Label>Amount before GST (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="site-expense-amount" /></div>
            <div><Label>GST on the bill (₹)</Label><Input type="number" step="0.01" min="0" value={form.gst_amount} onChange={set("gst_amount")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Paid from</Label>
            <Select value={form.paid_from} onValueChange={(v) => setForm({ ...form, paid_from: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PETTY_CASH">Site petty cash (balance {inr(balance)})</SelectItem>
                <SelectItem value="BANK">Company bank / UPI</SelectItem>
              </SelectContent>
            </Select>
            {tooMuch && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">More than the petty cash balance — ask for a top-up, or choose Company bank.</p>}
          </div>
          <div><Label>Bill photo (optional)</Label><Input ref={fileRef} type="file" accept="image/*,.pdf" className="mt-1.5" /></div>
          <div className="rounded-lg bg-muted/50 p-2.5 text-sm"><div className="flex justify-between font-semibold"><span>Total paid</span><span className="font-mono">{inr(total)}</span></div></div>
          <DialogFooter><Button type="submit" disabled={saving || tooMuch}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save expense</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PettyCashDialog({ projectId, balance, onSaved }) {
  const EMPTY = { entry_date: today(), kind: "TOPUP", amount: "", holder: "", reference: "", remarks: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/petty-cash`, { ...form, holder: form.holder || null, reference: form.reference || null, remarks: form.remarks || null });
      toast.success(form.kind === "TOPUP" ? "Petty cash given to the site" : "Petty cash returned to the bank");
      setForm(EMPTY);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="petty-cash-button"><Wallet className="h-4 w-4" /> Petty cash</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Petty cash — balance {inr(balance)}</DialogTitle>
          <DialogDescription>Give money to the site in-charge from the bank, or record unused cash coming back.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="TOPUP">Give to site (top-up)</SelectItem><SelectItem value="RETURN">Returned to office</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="petty-amount" /></div>
            <div><Label>Given to (site in-charge)</Label><Input value={form.holder} onChange={set("holder")} className="mt-1.5" /></div>
          </div>
          <div><Label>UTR / cheque no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
